import React, { useState } from 'react';
import { Code, ChevronDown, ChevronUp, ExternalLink, Server, Database, Zap } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations/canvaTranslations';

const TechnicalSection = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [expandedSections, setExpandedSections] = useState({
    api: false,
    architecture: false,
    integration: false,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const codeExample = `// Based on iHerb WeChat API Integration Real-World Experience
const createSupportTicket = async (userMessage) => {
  const ticketData = {
    ticket: {
      subject: \`WeChat User Inquiry: \${userMessage.substring(0, 50)}...\`,
      comment: { 
        body: \`Source: WeChat\\nUser Question: \${userMessage}\\nAssigned: Global Support Team\` 
      },
      priority: "normal",
      tags: ["wechat", "china-user", "auto-routed", "canva-demo"]
    }
  };
  
  // Using mock API call - Following enterprise development best practices
  return await mockZendeskAPI.createTicket(ticketData);
};`;

  const techStack = [
    { icon: <Server className="w-6 h-6" />, name: 'Node.js', description: t.technical.backendServer },
    { icon: <Database className="w-6 h-6" />, name: 'PostgreSQL', description: t.technical.messageStorage },
    { icon: <Zap className="w-6 h-6" />, name: 'Redis', description: t.technical.cachingLayer },
    { icon: <ExternalLink className="w-6 h-6" />, name: 'WeChat API', description: t.technical.officialWeChatApi },
    { icon: <ExternalLink className="w-6 h-6" />, name: 'Zendesk API', description: t.technical.zendeskRestApi },
  ];

  return (
    <section
      id="technical"
      className="min-h-screen py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-gradient-to-br from-[#00C4CC] to-[#6C47FF] rounded-2xl">
              <Code className="w-12 h-12 text-white" />
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            {t.technical.title}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#00C4CC] to-[#6C47FF] mx-auto rounded-full"></div>
          <p className="text-xl text-gray-300 mt-6 max-w-3xl mx-auto">
            {t.technical.description}
          </p>
        </div>

        {/* Professional Qualifications Section */}
        <div className="mb-12 bg-gray-800 rounded-2xl p-8 shadow-xl">
          <h3 className="text-2xl font-bold mb-6 text-center text-white">{t.technical.qualifications}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-700 rounded-xl p-6">
              <h4 className="text-xl font-semibold text-[#00C4CC] mb-4">{t.technical.certifications}</h4>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-[#00C4CC] mr-2">•</span>
                  <span>{t.technical.pmpCertified}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#00C4CC] mr-2">•</span>
                  <span>{t.technical.bilingual}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#00C4CC] mr-2">•</span>
                  <span>{t.technical.dualMasters}</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-700 rounded-xl p-6">
              <h4 className="text-xl font-semibold text-[#6C47FF] mb-4">{t.technical.techStack}</h4>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-[#6C47FF] mr-2">•</span>
                  <span>{t.technical.realWorldStack}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#6C47FF] mr-2">•</span>
                  <span>{t.technical.toolEcosystem}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#6C47FF] mr-2">•</span>
                  <span>{t.technical.experience}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Tech Stack Overview */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold mb-6 text-center">{t.technical.techStackTitle}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {techStack.map((tech, index) => (
              <div
                key={index}
                className="bg-gray-800 rounded-xl p-6 text-center hover:bg-gray-700 transition-all duration-300 transform hover:scale-105"
              >
                <div className="text-[#00C4CC] mb-3 flex justify-center">
                  {tech.icon}
                </div>
                <h4 className="font-semibold mb-2">{tech.name}</h4>
                <p className="text-sm text-gray-400">{tech.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Collapsible Code Sections */}
        <div className="space-y-4">
          {/* API Integration Example */}
          <div className="bg-gray-800 rounded-xl overflow-hidden shadow-2xl">
            <button
              onClick={() => toggleSection('api')}
              className="w-full px-6 py-4 bg-gray-700 hover:bg-gray-600 flex items-center justify-between transition-all duration-300"
            >
              <div className="flex items-center space-x-3">
                <Code className="w-5 h-5 text-[#00C4CC]" />
                <span className="font-semibold text-lg">{t.technical.apiExample}</span>
              </div>
              {expandedSections.api ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </button>
            {expandedSections.api && (
              <div className="p-6 bg-gray-900">
                <div className="mb-4 p-4 bg-blue-900/30 border-l-4 border-blue-500 rounded">
                  <p className="text-sm text-blue-200">
                    {t.technical.apiExampleNote}
                  </p>
                </div>
                <pre className="overflow-x-auto">
                  <code className="text-sm text-gray-300">{codeExample}</code>
                </pre>
              </div>
            )}
          </div>

          {/* Architecture Details */}
          <div className="bg-gray-800 rounded-xl overflow-hidden shadow-2xl">
            <button
              onClick={() => toggleSection('architecture')}
              className="w-full px-6 py-4 bg-gray-700 hover:bg-gray-600 flex items-center justify-between transition-all duration-300"
            >
              <div className="flex items-center space-x-3">
                <Server className="w-5 h-5 text-[#6C47FF]" />
                <span className="font-semibold text-lg">{t.technical.systemArchitecture}</span>
              </div>
              {expandedSections.architecture ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </button>
            {expandedSections.architecture && (
              <div className="p-6 bg-gray-900">
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <span className="text-[#00C4CC] mr-2">•</span>
                    <span>{t.technical.microservices}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#00C4CC] mr-2">•</span>
                    <span>{t.technical.messageQueue}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#00C4CC] mr-2">•</span>
                    <span>{t.technical.restfulApi}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#00C4CC] mr-2">•</span>
                    <span>{t.technical.realtimeSync}</span>
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Integration Points */}
          <div className="bg-gray-800 rounded-xl overflow-hidden shadow-2xl">
            <button
              onClick={() => toggleSection('integration')}
              className="w-full px-6 py-4 bg-gray-700 hover:bg-gray-600 flex items-center justify-between transition-all duration-300"
            >
              <div className="flex items-center space-x-3">
                <ExternalLink className="w-5 h-5 text-[#00C4CC]" />
                <span className="font-semibold text-lg">{t.technical.integrationPoints}</span>
              </div>
              {expandedSections.integration ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </button>
            {expandedSections.integration && (
              <div className="p-6 bg-gray-900">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-800 rounded-lg p-4">
                    <h4 className="font-semibold text-[#00C4CC] mb-2">{t.technical.wechatIntegration}</h4>
                    <p className="text-sm text-gray-400">
                      {t.technical.wechatIntegrationDesc}
                    </p>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-4">
                    <h4 className="font-semibold text-[#6C47FF] mb-2">{t.technical.zendeskIntegration}</h4>
                    <p className="text-sm text-gray-400">
                      {t.technical.zendeskIntegrationDesc}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechnicalSection;


